<template>
  <div class="fd-list-page">
    <v-snackbar v-model="showSnackBar" color="#0d3320" top right timeout="3000">
      <v-icon left color="white">mdi-check-circle</v-icon>
      <span style="color: white">{{ msg }}</span>
      <template v-slot:action>
        <v-btn icon small @click="showSnackBar = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#16a34a"
      spinner="spinner"
    />

    <!-- Page Header -->
    <div class="fd-page-header">
      <div>
        <div class="fd-page-title">REFUNDED APPLICATIONS</div>
        <div class="fd-page-subtitle">Applications whose refund has been paid by DD or cheque</div>
      </div>
      <div class="fd-header-badge" v-if="list.length > 0">
        <v-icon color="#16a34a" size="18">mdi-filmstrip</v-icon>
        <span>{{ list.length }} Applications</span>
      </div>
    </div>

    <!-- Filter Card -->
    <div class="fd-filter-card mb-4">
      <v-row dense align="center">
        <!-- From Date -->
        <v-col cols="12" sm="4" md="3">
          <v-menu
            ref="menu1"
            v-model="menu1"
            :close-on-content-click="false"
            max-width="290"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                clearable
                dense
                outlined
                readonly
                hide-details
                v-model="fromDate"
                v-bind="attrs"
                background-color="white"
                v-on="on"
                @click:clear="fromDate = ''"
                label="From Date"
                class="fd-filter-input"
                prepend-inner-icon="mdi-calendar-start"
              >
                <template v-slot:append>
                  <v-icon color="black">mdi-chevron-down</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              color="#16a34a"
              @change="menu1 = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <!-- To Date -->
        <v-col cols="12" sm="4" md="3">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            max-width="290"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                clearable
                dense
                outlined
                readonly
                hide-details
                v-model="toDate"
                v-bind="attrs"
                background-color="white"
                v-on="on"
                @click:clear="toDate = ''"
                label="To Date"
                class="fd-filter-input"
                prepend-inner-icon="mdi-calendar-end"
                :disabled="!fromDate"
              >
                <template v-slot:append>
                  <v-icon color="black">mdi-chevron-down</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              color="#16a34a"
              @change="menu2 = false"
              :min="fromDate"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <!-- Search Keyword -->
        <v-col cols="12" sm="4" md="4">
          <v-text-field
            v-model="keyword"
            outlined
            dense
            hide-details
            clearable
            label="Search Application Number"
            prepend-inner-icon="mdi-magnify"
            class="fd-filter-input"
            @click:clear="keyword = ''"
          ></v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- Table Card -->
    <div class="fd-table-card mb-4">
      <v-data-table
        :headers="headers"
        :items="list"
        :loading="appLoading"
        :hide-default-footer="true"
        :mobile-breakpoint="0"
        class="fd-data-table"
        no-data-text="No applications found"
      >
        <!-- Application No Column -->
        <template v-slot:item.applicationNo="{ item }">
          <div class="fd-appno-cell">
            <div class="fd-appno-badge">
              <v-icon size="14" color="#16a34a" class="mr-1">mdi-file-document-outline</v-icon>
              <span class="fd-hash">#</span><span>{{ item.applicationNo || 'N/A' }}</span>
            </div>
            <div v-if="item.isExtended && item.originalApplication && item.originalApplication.applicationNo" class="fd-ref-chip">
              <v-icon size="11" color="#1e40af">mdi-link-variant</v-icon>
              <span>Ref: #{{ item.originalApplication.applicationNo }}</span>
            </div>
          </div>
        </template>

        <!-- Applicant Details Column -->
        <template v-slot:item.applicant="{ item }">
          <div class="fd-applicant-cell">
            <div class="fd-applicant-name">
              {{ item.applicantName || item.name || item.username || 'N/A' }}
            </div>
            <div class="fd-applicant-district" v-if="item.applicantDistrict || item.applicantAddress || item.placeOfShooting || item.state">
              <v-icon size="12" color="#16a34a">mdi-map-marker-outline</v-icon>
              <span>{{ item.applicantDistrict || item.applicantAddress || item.placeOfShooting || item.state }}</span>
            </div>
          </div>
        </template>

        <!-- Contact Column -->
        <template v-slot:item.contact="{ item }">
          <div class="fd-contact-cell">
            <div class="fd-contact-row" v-if="item.applicantMobileNo || item.mobileNumber || item.phone">
              <v-icon size="13" color="#64748b">mdi-phone-outline</v-icon>
              <span>{{ item.applicantMobileNo || item.mobileNumber || item.phone }}</span>
            </div>
            <div class="fd-contact-row" v-if="item.applicantEmail || item.email">
              <v-icon size="13" color="#64748b">mdi-email-outline</v-icon>
              <span class="fd-contact-email" :title="item.applicantEmail || item.email">{{ item.applicantEmail || item.email }}</span>
            </div>
            <div v-if="!item.applicantMobileNo && !item.mobileNumber && !item.phone && !item.applicantEmail && !item.email" style="color: #94a3b8; font-size: 12px;">
              —
            </div>
          </div>
        </template>

        <!-- Shooting Details Column -->
        <template v-slot:item.shootingDetails="{ item }">
          <div class="fd-shooting-cell">
            <div class="fd-shooting-title" v-if="item.titleOfFilm || item.filmName || (item.filmShootingDetails && item.filmShootingDetails[0] && item.filmShootingDetails[0].filmName) || item.natureOfApplication">
              {{ item.titleOfFilm || item.filmName || (item.filmShootingDetails && item.filmShootingDetails[0] && item.filmShootingDetails[0].filmName) || item.natureOfApplication }}
            </div>
            <div class="fd-shooting-info" v-if="item.filmShootingDetails && item.filmShootingDetails.length">
              <v-icon size="13" color="#16a34a">mdi-map-marker-multiple</v-icon>
              <span>{{ item.filmShootingDetails.length }} Location(s)</span>
            </div>
            <div class="fd-shooting-info" v-if="item.crewMembersCount || (item.filmShootingDetails && item.filmShootingDetails[0] && item.filmShootingDetails[0].noOfMembers)">
              <v-icon size="13" color="#64748b">mdi-account-group-outline</v-icon>
              <span>{{ item.crewMembersCount || item.filmShootingDetails[0].noOfMembers }} Members</span>
            </div>
          </div>
        </template>

        <!-- Fees Column -->
        <template v-slot:item.fees="{ item }">
          <div class="fd-fee-cell">
            <div class="fd-fee-row">
              <span class="fd-fee-label">Shooting:</span>
              <span class="fd-fee-value">₹{{ (item.shootingfee || 0).toLocaleString() }}</span>
            </div>
            <div class="fd-fee-row" v-if="item.installationfee">
              <span class="fd-fee-label">Install:</span>
              <span class="fd-fee-value">₹{{ (item.installationfee || 0).toLocaleString() }}</span>
            </div>
            <div class="fd-fee-row">
              <span class="fd-fee-label">Refund:</span>
              <span class="fd-fee-value" style="color: #059669;">₹{{ (item.refundAmount || (item.shootingfee || 0) + (item.installationfee || 0)).toLocaleString() }}</span>
            </div>
            <div class="fd-fee-row" v-if="item.refundtypenumber">
              <span class="fd-fee-label">{{ item.refundtype }}:</span>
              <span class="fd-fee-value">{{ item.refundtypenumber }}</span>
            </div>
          </div>
        </template>

        <!-- Status Column -->
        <template v-slot:item.status="{ item }">
          <div class="fd-status-wrapper">
            <span class="fd-badge" :class="statusClass(item)">{{ statusOf(item).label }}</span>
          </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <div class="fd-actions-cell">
            <button
              class="fd-btn-view"
              @click="$router.push('/FilmDetailedView?id=' + item._id)"
              title="View Details"
            >
              <v-icon size="15" color="#166534" class="mr-1">mdi-eye-outline</v-icon>
              <span>VIEW</span>
            </button>
            <button
              class="fd-btn-timeline"
              @click="openTimeline(item)"
              title="View Application Timeline"
            >
              <v-icon size="15" color="#15803d" class="mr-1">mdi-timeline-clock-outline</v-icon>
              <span>TIMELINE</span>
            </button>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="fd-empty-state">
            <v-icon size="48" color="#cbd5e1">mdi-folder-open-outline</v-icon>
            <div class="fd-empty-title">No Applications Found</div>
            <div class="fd-empty-desc">There are no applications matching your filter criteria.</div>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination Footer -->
      <div class="fd-table-footer" v-if="pages > 1">
        <span class="fd-footer-count">Page {{ currentPage }} of {{ pages }}</span>
        <v-pagination
          prev-icon="mdi-chevron-left"
          next-icon="mdi-chevron-right"
          :length="pages"
          :total-visible="7"
          v-model="currentPage"
          color="#16a34a"
          dense
        ></v-pagination>
      </div>
    </div>
        <v-dialog :retain-focus="false" persistent v-model="agreementdialog" max-width="600px">
          <v-card>
            <v-layout wrap>
              <v-flex>
                {{
                  agreement
                }}
              </v-flex>
            </v-layout>
          </v-card>
        </v-dialog>
  
  
        <v-dialog :retain-focus="true" persistent v-model="procceddialog" max-width="730px">
          <v-card>
            <v-layout wrap justify-center>
              <v-flex xs8 text-left pt-5>
                <span class="mainfont" style="color: black; font-size: 18px; font-weight: lighter">
                  Are you sure you want
                  to proceed this application for
                  <br />
                  1.&nbsp;Fee Payment
                  <br />
                  2.&nbsp;Agreement Execution
                </span>
              </v-flex>
            </v-layout>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="black" text @click="procceddialog = false" class="mainfont">Cancel</v-btn>
              <v-btn color="red" class="mainfont" text @click="proceed(appids2)">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog :retain-focus="true" persistent v-model="deletedialog" max-width="700px">
          <v-card>
            <v-layout wrap justify-center>
              <v-flex xs8 text-left pt-5>
                <span class="mainfont" style="color: black; font-size: 18px; font-weight: lighter">
                  Are you sure you want
                  to forward?
                </span>
  
              </v-flex>
              <v-flex xs8 pt-6 pr-4 class="mainfont">
                <v-autocomplete outlined dense color="black" :items="usernames" v-model="selectedUsername" item-text="name"
                  placeholder="Select a Role" style="font-family: 'Inter', sans-serif;" item-value="name" item-color="#FF1313"
                  hide-details clearable class="custom-autocomplete">
                  <template v-slot:append>
                    <v-icon color="black">mdi-chevron-down</v-icon>
                  </template>
                </v-autocomplete>
              </v-flex>
  
              <v-flex xs8 pt-5 pr-4 class="mainfont">
                <v-text-field placeholder="reason" v-model="reason" dense class="mainfont" outlined color="black">
                </v-text-field>
              </v-flex>
            </v-layout>
  
  
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="black" text @click="deletedialog = false" class="mainfont">Cancel</v-btn>
              <v-btn color="red" class="mainfont" text @click="forwardToUsers2(appids)">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
  
  
        <v-dialog :retain-focus="true" persistent v-model="deletedialog" max-width="700px">
          <v-card>
            <v-layout wrap justify-center>
              <v-flex xs8 text-left pt-5>
                <span class="mainfont" style="color: black; font-size: 18px; font-weight: lighter">
                  Are you sure you want
                  to forward?
                </span>
  
              </v-flex>
              <v-flex xs8 pt-6 pr-4 class="mainfont">
                <v-autocomplete outlined dense color="black" :items="usernames" v-model="selectedUsername" item-text="name"
                  placeholder="Select a Role" style="font-family: 'Inter', sans-serif;" item-value="name" item-color="#FF1313"
                  hide-details clearable class="custom-autocomplete">
                  <template v-slot:append>
                    <v-icon color="black">mdi-chevron-down</v-icon>
                  </template>
                </v-autocomplete>
              </v-flex>
  
              <v-flex xs8 pt-5 pr-4 class="mainfont">
                <v-text-field placeholder="reason" v-model="reason" dense class="mainfont" outlined color="black">
                </v-text-field>
              </v-flex>
            </v-layout>
  
  
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="black" text @click="deletedialog = false" class="mainfont">Cancel</v-btn>
              <v-btn color="red" class="mainfont" text @click="forwardToUsers2(appids)">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>

      <!-- Timeline Dialog -->
      <TimelineDialog
        v-model="timelineDialog"
        :application="selectedAppForTimeline"
      />
    </div>
  </template>
  <script>
  import axios from "axios";
  import moment from "moment";
  import TimelineDialog from "@/components/Common/TimelineDialog.vue";
  import { statusOf } from "@/components/Common/deskConfig";
  // import CryptoJS from "crypto-js";
  export default {
    components: {
      TimelineDialog,
    },
    data() {
      return {
        timelineDialog: false,
        selectedAppForTimeline: null,
        headers: [
          { text: "Application No", value: "applicationNo", sortable: false, width: "160px" },
          { text: "Applicant", value: "applicant", sortable: false },
          { text: "Contact", value: "contact", sortable: false },
          { text: "Shooting Details", value: "shootingDetails", sortable: false },
          { text: "Fees (₹)", value: "fees", sortable: false },
          { text: "Status", value: "status", sortable: false, width: "130px" },
          { text: "Actions", value: "actions", sortable: false, align: "center", width: "230px" },
        ],
        path: localStorage.getItem("path"),
        curIdToDelete: null,
        name: null,
        reason: null,
        editdialog: false,
        barlist: [],
        // reason:null,
        edit: [],
        userProfileTabs: null,
        procceddialog: false,
        subtab: null,
        appLoading: false,
        page: 1,
        keyArray: [],
        pages: 0,
        usernames: [],
        selectedUsername: null,
        dialog1: false,
        preview: null,
        agreementdialog: false,
        agreement: null,
        rejectdialog: false,
        tab: null,
        barlist1: [],
        gender: ["Male", "Female", "Transgender"],
        dialog3: false,
        deletedialog: false,
        minAmount: null,
        password: null,
        categoryarray: [],
        list: [],
        flag: false,
        flag2: false,
        auctionbased: [],
        menu1: false,
        cdate: null,
        menu2: false,
        menu3: false,
        menu4: false,
        fromDate: null,
        toDate: null,
        fromDate2: null,
        toDate2: null,
        msg: null,
        currentPage: 1,
        showSnackBar: false,
        users: [],
        range: [],
        username1: null,
        username2: null,
        rangeid: null,
        divisionid: null,
        dialog2: false,
        viewdialog: false,
        keyword: "",
      };
    },
  
    mounted() {
      this.getList();
      this.getRange();
      // this.getUsers();
    },
    watch: {
      currentPage() {
        this.getList();
      },
      fromDate() {
        this.getList();
      },
      toDate() {
        this.getList();
      },
      keyword() {
        this.getList();
      },
    },
    computed: {
      getUserType() {
        const uType = localStorage.getItem("uType");
  
        if (uType === "777") {
          return "admin";
        } else if (uType === "772") {
          return "user";
        } else if (uType === "7741") {
          return "section";
        }
        else if (uType === "7742") {
          return "superclerk";
        }
        else {
          return "division";
        }
      },
      minDate() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      },
      formattedDate() {
        const date = new Date(this.edit.selectedDate);
        return date.toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      },
    },
  
  
    methods: {
      statusOf,
      statusClass(item) {
        const tone = statusOf(item).tone;
        return tone === "danger" ? "fd-badge-rejected" : tone === "success" ? "fd-badge-approved" : "fd-badge-pending";
      },
      openTimeline(item) {
        this.selectedAppForTimeline = item;
        this.timelineDialog = true;
      },
    //   navigateToFilmDetailedView(id) {
      // Encrypt the ID before navigating
    //   const encryptedId = CryptoJS.AES.encrypt(id.toString(), 'encryptionSecret').toString();
    //   this.$router.push({
    //     path: '/FilmDetailedView',
    //     query: {
    //       id: encryptedId
    //     }
    //   });
    // },
      setCurIdToDelete(id) {
        this.curIdToDelete = id;
      },
  
      settoforward(id) {
        this.curIdToDelete = id;
      },
  
      proceed() {
        const idToDelete = this.curIdToDelete;
        if (!idToDelete) {
          return;
        }
        axios({
          url: "/division/payment/initiation/",
          method: "POST",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: idToDelete,
            reason: this.reason,
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status == true) {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.deletedialog = false;
              window.location.reload();
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
      refund(appid) {
  
        axios({
          url: "/refund/",
          method: "POST",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: appid,
            rangeid: this.rangeid,
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status == true) {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              window.location.reload();
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
      forwardToRange(appid) {
  
        axios({
          url: "/range/forward/",
          method: "POST",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: appid,
            rangeid: this.rangeid,
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status == true) {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.deletedialog = false;
              window.location.reload();
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
  
      getRange() {
        this.appLoading = true;
  
        axios({
          method: "post",
          url: "/get/range",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            divisionid: this.divisionid,
            path: this.path,
          },
        })
          .then((response) => {
            this.range = response.data.data;
            this.appLoading = false;
          })
          .catch((err) => {
            this.ServerError = true;
            console.log(err);
          });
      },
  
      deleteItem() {
        const idToDelete = this.curIdToDelete;
        if (!idToDelete) {
          return;
        }
        axios({
          url: "/division/reject",
  
          method: "POST",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: idToDelete,
            reason: this.reason,
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status == true) {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              this.deletedialog = false;
              window.location.reload();
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      },
      forwardToUsers(_id) {
        this.getUsers(_id);
        this.appids = _id;
      },
      forwardToUsersone(_id) {
        this.getUsersone(_id);
        this.appids = _id;
      },
  
  
      forwardToUsers2(appids) {
        this.forward(appids);
      },
      forwardToUsers3(appids2) {
        this.forward2(appids2);
      },
  
      formatDate(item) {
        var dt = new Date(item);
        var year = dt.getFullYear();
        dt = dt.toString();
        var strTime = dt.slice(4, 10) + " " + year;
        return strTime;
      },
      getUsersone(appid) {
        axios({
          method: "post",
          url: "/dfo/forward/list",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: appid,
          },
        })
          .then((response) => {
            const sups = (response.data.superintendents && response.data.superintendents.length > 0)
              ? response.data.superintendents
              : (response.data.superintendent ? [response.data.superintendent] : []);
            this.usernames = sups.map((s) => ({
              name: `${s.username || s.name} (Superintendent)`,
              role: "Superintendent",
              userId: s._id,
            }));
          })
          .catch((err) => {
            this.ServerError = true;
            console.log(err);
          });
      },
  
      getUsers(appid) {
        axios({
          method: "post",
          url: "/dfo/forward/list",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: appid,
          },
        })
          .then((response) => {
            const list = [];
            if (response.data.user) {
              list.push({
                name: `${response.data.user.username || response.data.user.name} (User)`,
                role: "User",
                userId: response.data.user._id,
              });
            }
            const sups = (response.data.superintendents && response.data.superintendents.length > 0)
              ? response.data.superintendents
              : (response.data.superintendent ? [response.data.superintendent] : []);
            sups.forEach((s) => {
              list.push({
                name: `${s.username || s.name} (Superintendent)`,
                role: "Superintendent",
                userId: s._id,
              });
            });
            this.usernames = list;
          })
          .catch((err) => {
            this.ServerError = true;
            console.log(err);
          });
      },
      forward(appids) {
        if (!this.selectedUsername) {
          this.$refs.snackbar.showSnackbar("Please select a user to forward to.");
          return;
        }
        const selectedUser = this.usernames.find(user => user.name === this.selectedUsername);
        if (!selectedUser) {
          return;
        }
  
        axios({
          method: "post",
          url: "/forward/",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: appids,
            forwardto: selectedUser.userId,
            reason: this.reason,
          }
        })
          .then((response) => {
            if (response.data.status == true) {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              window.location.reload();
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          })
          .catch((err) => {
            this.ServerError = true;
            console.error(err);
          });
      },
  
      forward2(appids2) {
  
        axios({
          method: "post",
          url: "/division/payment/initiation/",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            appid: appids2,
  
          }
        })
          .then((response) => {
            if (response.data.status == true) {
              this.msg = response.data.msg;
              this.showSnackBar = true;
              window.location.reload();
            } else {
              this.msg = response.data.msg;
              this.showSnackBar = true;
            }
          })
          .catch((err) => {
            this.ServerError = true;
            console.error(err);
          });
      },
  
      // getList() {
      //   this.appLoading = true;
      //   axios({
      //     method: "POST",
      //     url: "/forwarded/apps",
      //     headers: {
      //       token: localStorage.getItem("token"),
      //     },
      //     data: {
      //       status: "yes",
      //       categoryId: this.categoryId,
      //       page: this.currentPage,
      //       fromDate: this.fromDate,
      //       toDate: this.toDate,
      //       limit: 10,
      //     },
      //   })
      //     .then((response) => {
      //       this.list = response.data.data.map((item) => {
      //         const date = new Date(item.selectedDate);
      //         item.selectedDate = date.toLocaleDateString();
      //         item.cdate = moment(item.selectedDate).format("YYYY-MM-DD");
      //         return item;
      //       });
      //       this.totalData = response.data.totalLength;
      //       this.selectedDatesArray = this.list.map((item) => item.selectedDate);
      //       this.pages = Math.ceil(this.totalData / response.data.limit);
      //       this.appLoading = false;
      //     })
      //     .catch((err) => {
      //       this.ServerError = true;
      //       console.log(err);
      //     });
      // },
      getList() {
        this.appLoading = true;
  
        // Convert fromDate and toDate to timestamp format
  
        // var formattedFromDate = new Date(this.fromDate).toISOString();
        // if (this.toDate != null) {
        //   var formattedToDate = new Date(this.toDate).toISOString();
        // }
        // var data = {};
        // data['status'] = "yes";
        // if(this.cate)
        // data['categoryId'] = this.categoryId;
  
  
        axios({
          method: "GET",
          url: "/challan/refunded-applications",
          headers: {
            token: localStorage.getItem("token"),
          },
          params: {

          },
          // data: {
          //   task: "T6",
          //   status: "yes",
          //   categoryId: this.categoryId,
          //   page: this.currentPage,
          //   fromDate: this.fromDate,
          //   toDate: this.toDate,
          //   applicationNo: this.keyword,
          //   limit: 5,
          // },
        })
          .then((response) => {
            this.list = response.data.data.map((item) => {
              const date = new Date(item.selectedDate);
              item.selectedDate = date.toLocaleDateString();
              item.cdate = moment(item.selectedDate).format("YYYY-MM-DD");
              return item;
            });
            this.selectedDatesArray = this.list.map((item) => item.selectedDate);
            this.totalData = response.data.totalLength;
            this.pages = Math.ceil(this.totalData / response.data.limit);
            this.appLoading = false;
          })
          .catch((err) => {
            this.ServerError = true;
            console.log(err);
          });
      },
  
  
    },
  };
  </script>
        
  <style>
  .subheading {
    font-family: "Inter", sans-serif;
    color: #42275a;
    text-transform: none;
    font-size: 17px;
    font-weight: bold;
  
  }
  
  .heading {
    font-family: "Inter", sans-serif;
    color: rgb(229, 111, 8);
    text-transform: none;
    font-size: 25px;
  
  }
  </style>